import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, IconButton, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { CustomerRevenueReportItem, Currency } from '@vy/types';
import { DEFAULT_CURRENCY } from '@vy/types';
import { isEmptyString, shortenString } from '@vy/utils';
import type { KeyboardEvent, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { formatNumberWithPrecision } from '../../../../../utils/number';
import { getTotalCustomerRevenueDateHeaders } from '../../../reports-page/reports-page-utils/customer-revenue-report-utils/get-total-revenue-dates';
import {
  getNumericRevenueForDate,
  getRowRecognizedTotal,
  getRowRemainingTotal,
} from './customer-revenue-report-calculations';
import { filterDateHeaders } from './filter-date-headers';
import {
  getCustomerRevenueProductName,
  getRevenueTypeLabel,
  ROW_LEVELS,
  type ExpandedCustomerRevenueReportRow,
} from './customer-revenue-report-row-expander';

const DASHES_CELL = <VyTableCell value="--" containerClassName="justify-start" />;
const EMPTY_CELL = <VyTableCell value="" containerClassName="justify-start" />;

function renderTotalsCell(value: number) {
  if (value === 0) {
    return DASHES_CELL;
  }

  return <VyTableCell value={formatNumberWithPrecision(value, 2)} containerClassName="justify-start font-bold" />;
}

export const CUSTOMER_REVENUE_REPORT_KEYS = Object.freeze({
  EXPAND: 'expand',
  CUSTOMER_NAME: 'customerName',
  CONTRACT: 'contract',
  PRODUCT: 'product',
  COUNTRY: 'country',
  STATE: 'state',
  RECOGNIZED: 'recognized',
  REMAINING: 'remaining',
} satisfies Record<string, string>);

export const CUSTOMER_REVENUE_REPORT_DATE_COLUMN_CLASSNAME = 'vy-customer-revenue-date-column';

type CustomerRevenueReportRow = ExpandedCustomerRevenueReportRow;

const EXPANDABLE_CELL_STYLE = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
} as const;

function ExpandableCell({ onToggle, children }: { onToggle: () => void; children: ReactNode }) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div role="button" tabIndex={0} onClick={onToggle} onKeyDown={handleKeyDown} style={EXPANDABLE_CELL_STYLE}>
      {children}
    </div>
  );
}

function renderCustomerAttributeCell(row: CustomerRevenueReportRow, value: string | null | undefined) {
  if (row.level !== ROW_LEVELS.Product) {
    return EMPTY_CELL;
  }

  if (isEmptyString(value)) {
    return DASHES_CELL;
  }

  return <VyTableCell value={value} containerClassName="justify-start" />;
}

function buildBaseColumns(
  onOpenEditDialog?: (row: CustomerRevenueReportRow) => void,
): GridColDef<CustomerRevenueReportRow>[] {
  return [
  {
    field: CUSTOMER_REVENUE_REPORT_KEYS.EXPAND,
    headerName: '',
    width: 64,
    maxWidth: 64,
    sortable: false,
    hideable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const indentByLevel: Record<string, number> = {
        [ROW_LEVELS.Product]: 0,
        [ROW_LEVELS.RevenueType]: 24,
        [ROW_LEVELS.Total]: 0,
        [ROW_LEVELS.TotalChild]: 24,
      };
      const indent = indentByLevel[params.row.level] ?? 0;

      return (
        <div style={{ paddingLeft: indent, display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row.isParent && (
            <IconButton size="small" onClick={() => params.row.toggleExpand?.(params.row.id)}>
              <KeyboardArrowDownIcon
                sx={{
                  width: 16,
                  height: 16,
                  opacity: 1,
                  transform: params.row.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                }}
              />
            </IconButton>
          )}
        </div>
      );
    },
  },
  {
    field: CUSTOMER_REVENUE_REPORT_KEYS.CUSTOMER_NAME,
    headerName: 'Customer',
    flex: 1,
    minWidth: 200,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => row.customer?.name ?? '',
    renderCell: ({ row }) => {
      const toggle = () => row.toggleExpand?.(row.id);

      if (row.level === ROW_LEVELS.Total) {
        return (
          <ExpandableCell onToggle={toggle}>
            <VyTableCell value="Recognized" containerClassName="justify-start font-bold" />
          </ExpandableCell>
        );
      }

      if (row.level === ROW_LEVELS.TotalChild) {
        return <VyTableCell value={getRevenueTypeLabel(row.revenueType)} containerClassName="justify-start font-bold" />;
      }

      // Product rows are the top level now — show the (denormalized) customer
      // name and keep the expander that reveals the revenue-type breakdown.
      if (row.level === ROW_LEVELS.Product) {
        return (
          <ExpandableCell onToggle={toggle}>
            <VyTableCell value={row.customer.name} containerClassName="justify-start" />
          </ExpandableCell>
        );
      }

      if (row.level === ROW_LEVELS.RevenueType) {
        return <VyTableCell value={getRevenueTypeLabel(row.revenueType)} containerClassName="justify-start" />;
      }

      return <VyTableCell value="" containerClassName="justify-start" />;
    },
  },
  {
    field: CUSTOMER_REVENUE_REPORT_KEYS.CONTRACT,
    headerName: 'Contract',
    flex: 1,
    minWidth: 160,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => (row.level === ROW_LEVELS.Product ? row.contract?.id ?? '' : ''),
    renderCell: ({ row }) => {
      if (row.level !== ROW_LEVELS.Product) {
        return EMPTY_CELL;
      }

      const contractId = row.contract?.id;
      return (
        <VyTableCell
          value={contractId ? shortenString(contractId, 5) : '--'}
          containerClassName="justify-start"
        />
      );
    },
  },
  {
    field: CUSTOMER_REVENUE_REPORT_KEYS.PRODUCT,
    headerName: 'Product',
    flex: 1,
    minWidth: 220,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => (row.level === ROW_LEVELS.Product ? getCustomerRevenueProductName(row) : ''),
    renderCell: ({ row }) => {
      if (row.level !== ROW_LEVELS.Product) {
        return EMPTY_CELL;
      }

      const handleNameClick = (event: React.MouseEvent) => {
        if (!onOpenEditDialog) return;
        event.stopPropagation();
        onOpenEditDialog(row);
      };

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
          <Box
            onClick={handleNameClick}
            sx={{
              cursor: onOpenEditDialog ? 'pointer' : 'default',
              textDecoration: onOpenEditDialog ? 'underline dotted' : 'none',
              flex: 1,
              minWidth: 0,
            }}
            data-vy="customer-revenue-report-variant-name"
          >
            <VyTableCell value={getCustomerRevenueProductName(row)} containerClassName="justify-start" />
          </Box>
          {row.isOverridden && (
            <Tooltip title="This row has a manual revenue recognition override">
              <InfoOutlinedIcon
                fontSize="small"
                color="info"
                sx={{ flexShrink: 0 }}
                data-vy="customer-revenue-report-override-indicator"
              />
            </Tooltip>
          )}
        </Box>
      );
    },
  },
  {
    field: CUSTOMER_REVENUE_REPORT_KEYS.COUNTRY,
    headerName: 'Country',
    flex: 1,
    minWidth: 140,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => renderCustomerAttributeCell(row, row.customer.country),
  },
  {
    field: CUSTOMER_REVENUE_REPORT_KEYS.STATE,
    headerName: 'State',
    flex: 1,
    minWidth: 140,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => renderCustomerAttributeCell(row, row.customer.state),
  },
  ];
}

function renderPriceCell(row: CustomerRevenueReportRow, value: number) {
  const currency = (row.customer.currency ?? DEFAULT_CURRENCY) as Currency;

  if (value === 0) {
    return DASHES_CELL;
  }

  return (
    <Price currency={currency} align="left" fractionDigits={2}>
      {value}
    </Price>
  );
}

function buildSummaryColumns(allDateHeaders: string[]): GridColDef<CustomerRevenueReportRow>[] {
  return [
    {
      field: CUSTOMER_REVENUE_REPORT_KEYS.RECOGNIZED,
      headerName: 'Recognized',
      flex: 1,
      minWidth: 160,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => getRowRecognizedTotal(row, allDateHeaders),
      renderCell: ({ value, row }) => {
        if (row.revenueType === 'spacer') {
          return EMPTY_CELL;
        }
        if (row.level === ROW_LEVELS.Total || row.level === ROW_LEVELS.TotalChild) {
          return renderTotalsCell(value as number);
        }
        if (row.level === ROW_LEVELS.RevenueType && row.revenueType !== 'revenue') {
          return DASHES_CELL;
        }
        if (row.level === ROW_LEVELS.Product && row.isExpanded) {
          return DASHES_CELL;
        }
        return renderPriceCell(row, value as number);
      },
    },
    {
      field: CUSTOMER_REVENUE_REPORT_KEYS.REMAINING,
      headerName: 'Remaining',
      flex: 1,
      minWidth: 160,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => getRowRemainingTotal(row, allDateHeaders),
      renderCell: ({ value, row }) => {
        if (row.revenueType === 'spacer') {
          return EMPTY_CELL;
        }
        if (row.level === ROW_LEVELS.Total || row.level === ROW_LEVELS.TotalChild) {
          return renderTotalsCell(value as number);
        }
        if (row.level === ROW_LEVELS.RevenueType && row.revenueType !== 'revenue') {
          return DASHES_CELL;
        }
        if (row.level === ROW_LEVELS.Product && row.isExpanded) {
          return DASHES_CELL;
        }
        return renderPriceCell(row, value as number);
      },
    },
  ];
}

export function useCustomerRevenueReportColumns(
  reportItems: CustomerRevenueReportItem[],
  fromDate?: Date | null,
  recognizedUpTo?: Date | null,
  onOpenEditDialog?: (row: CustomerRevenueReportRow) => void,
): GridColDef<CustomerRevenueReportRow>[] {
  const baseColumns = useMemo(() => buildBaseColumns(onOpenEditDialog), [onOpenEditDialog]);
  const allDateHeaders = useMemo(() => getTotalCustomerRevenueDateHeaders(reportItems), [reportItems]);

  const dateHeaders = useMemo(
    () => filterDateHeaders(allDateHeaders, fromDate, recognizedUpTo),
    [allDateHeaders, fromDate, recognizedUpTo],
  );

  const buildDateCell = useCallback(() => {
    return ({ value, row }: { value?: unknown; row: CustomerRevenueReportRow }) => {
      const numericValue = value as number;
      const currency: Currency = row.customer.currency ?? DEFAULT_CURRENCY;

      if (row.revenueType === 'spacer') {
        return EMPTY_CELL;
      }

      if (row.level === ROW_LEVELS.Total || row.level === ROW_LEVELS.TotalChild) {
        return renderTotalsCell(numericValue);
      }

      if (numericValue === 0) {
        return DASHES_CELL;
      }

      return (
        <Price currency={currency} align="left" fractionDigits={2}>
          {numericValue}
        </Price>
      );
    };
  }, []);

  const dateColumns = useMemo(
    () =>
      dateHeaders.map<GridColDef<CustomerRevenueReportRow>>((dateHeader) => {
        const field = buildDateFieldId(dateHeader);

        return {
          field,
          headerName: dateHeader,
          flex: 1,
          minWidth: 160,
          type: 'number',
          headerAlign: 'left',
          align: 'left',
          valueGetter: (_value, row) => getNumericRevenueForDate(row, dateHeader, allDateHeaders),
          valueFormatter: value => formatNumberWithPrecision(Number(value ?? 0), 2),
          headerClassName: CUSTOMER_REVENUE_REPORT_DATE_COLUMN_CLASSNAME,
          cellClassName: CUSTOMER_REVENUE_REPORT_DATE_COLUMN_CLASSNAME,
          renderCell: buildDateCell(),
        };
      }),
    [dateHeaders, allDateHeaders, buildDateCell],
  );

  const summaryColumns = useMemo(() => buildSummaryColumns(allDateHeaders), [allDateHeaders]);

  return useMemo(() => [...baseColumns, ...dateColumns, ...summaryColumns], [baseColumns, dateColumns, summaryColumns]);
}

function buildDateFieldId(dateHeader: string) {
  return `date-${dateHeader.toLowerCase().replace(/\s+/g, '-')}`;
}
